import React, { FC, useEffect, useState } from 'react'
import type { AllotmentAdvanceRecord } from './ViewAllotmentAdvanceModal'

type Props = {
  visible: boolean
  records: AllotmentAdvanceRecord[]
  period: string
  onClose: () => void
  onReviewAll: (officeRemarks: string) => void
  isLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const AllotmentBulkReviewModal: FC<Props> = ({
  visible,
  records,
  period,
  onClose,
  onReviewAll,
  isLoading = false,
}) => {
  const [officeRemarks, setOfficeRemarks] = useState('')
  const [remarksError, setRemarksError] = useState('')

  useEffect(() => {
    if (!visible) return
    setOfficeRemarks('')
    setRemarksError('')
  }, [visible])

  const validateRemarks = () => {
    if (!officeRemarks || !officeRemarks.trim()) {
      setRemarksError('Office remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '80vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Bulk Review - Office</h5>
            <div className='text-muted small mt-1'>
              Review all {records.length} crew allotments for {period}
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <span className='badge badge-light-warning'>{records.length} Pending</span>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isLoading} />
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          {/* Crew List */}
          <div className='mb-4'>
            <label className='form-label fw-semibold fs-6 mb-2 text-dark'>
              Crew Members to Review:
            </label>
            <div className='border rounded p-3' style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {records.length === 0 ? (
                <div className='text-muted text-center py-3'>No crew members to review</div>
              ) : (
                <table className='table table-sm table-row-bordered'>
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th>#</th>
                      <th>Crew Name</th>
                      <th>Rank</th>
                      <th>Vessel</th>
                      <th>Master Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, index) => (
                      <tr key={record.crewId}>
                        <td>{index + 1}</td>
                        <td>{record.crewName}</td>
                        <td>{record.rankName || '-'}</td>
                        <td>{record.vesselName || '-'}</td>
                        <td className='text-truncate' style={{ maxWidth: '200px' }}>
                          {record.masterRemarks || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Office Remarks */}
          <div className='mb-4'>
            <label className={LABEL}>
              Office Remarks (applies to all) <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <textarea
              className='form-control'
              rows={4}
              value={officeRemarks}
              onChange={(event) => {
                setOfficeRemarks(event.target.value)
                if (remarksError) setRemarksError('')
              }}
              placeholder='Add office review remarks for all crew members'
              disabled={isLoading}
            />
            {remarksError && <div className='text-danger small mt-2'>{remarksError}</div>}
          </div>

          {/* Warning */}
          <div className='alert alert-danger d-flex align-items-start'>
            <i className='bi bi-exclamation-triangle-fill me-2 mt-1 fs-5'></i>
            <div>
              <strong>Important Note:</strong> Once the bulk review is submitted, you cannot perform further submissions for other crew members for this month. 
              If you want to review all crew members together, please wait until all crew submissions are ready. 
              Proceed only if you are sure you want to review and approve only these {records.length} crew members.
            </div>
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm'
            disabled={isLoading || records.length === 0}
            onClick={() => {
              if (!validateRemarks()) return
              onReviewAll(officeRemarks)
            }}
          >
            {isLoading ? 'Reviewing...' : `Approve All (${records.length})`}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AllotmentBulkReviewModal
